import { type JSX } from "react";
import { type AvailableModel } from "./useModelSelector";

// USD per 1M tokens (OpenAI Standard pricing, 2026-07-28 時点)。
// 新モデル追加時は useModelSelector.ts の AVAILABLE_MODELS も合わせて更新する。
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  "gpt-5.6-sol":  { input: 4.00,  output: 20.00 },
  "gpt-5.6-terra": { input: 2.00,  output: 12.00 },
  "gpt-5.5":      { input: 2.50,  output: 10.00 },
  "gpt-5.4":      { input: 2.50,  output: 15.00 },
  "gpt-5.4-mini": { input: 0.75,  output: 4.50 },
  "gpt-5.4-nano": { input: 0.20,  output: 1.25 },
  "gpt-4.1":      { input: 2.00,  output: 8.00 },
  "gpt-4.1-mini": { input: 0.40,  output: 1.60 },
  "gpt-4o":       { input: 2.50,  output: 10.00 },
  "gpt-4o-mini":  { input: 0.15,  output: 0.60 },
};
// 未登録モデル時のフォールバックは高めの値を採用し、意図せず過少見積もりを起こさないようにする
const FALLBACK_PRICING = { input: 2.50, output: 10.00 };

function estimateCost(inputTokens: number, outputTokens: number, model: string): number {
  const pricing = MODEL_PRICING[model] ?? FALLBACK_PRICING;
  return (inputTokens / 1_000_000) * pricing.input + (outputTokens / 1_000_000) * pricing.output;
}

export type DebugInfo = {
  // 既存フィールド（変更なし）
  playlineId: string | null;
  sceneNumber: number;
  maxScenes: number;
  lastGenerationMs: number | null;
  avgGenerationMs: number | null;
  estimatedRemainingMs: number | null;
  totalTokenInput: number;
  totalTokenOutput: number;
  model: string;
  onClearProgress: () => void;
  // 追加フィールド（task 4.1）
  selectedModel: AvailableModel;
  availableModels: readonly AvailableModel[];
  onModelChange: (model: AvailableModel) => void;
  genTimes: number[];
  recentErrors: string[];
  lastPromptSummary: string | null;
};

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export function DebugOverlay({
  playlineId,
  sceneNumber,
  maxScenes,
  lastGenerationMs,
  avgGenerationMs,
  estimatedRemainingMs,
  totalTokenInput,
  totalTokenOutput,
  model,
  onClearProgress,
  selectedModel,
  availableModels,
  onModelChange,
  genTimes,
  recentErrors,
  lastPromptSummary,
}: DebugInfo): JSX.Element {
  const remaining = maxScenes - sceneNumber;
  const cost = estimateCost(totalTokenInput, totalTokenOutput, model);

  // 最新が先頭となるよう逆順にして最大 5 件取得
  const recentGenTimes = [...genTimes].reverse().slice(0, 5);
  // エラーは最大 3 件（既にスタック管理されている前提で先頭から 3 件）
  const displayErrors = recentErrors.slice(0, 3);

  return (
    <div
      id="debug-overlay"
      data-testid="debug-overlay"
      role="region"
      aria-label="デバッグパネル"
      className="flex max-h-[70dvh] w-full max-w-[90vw] select-none flex-col rounded-lg border border-reader-debug-border bg-reader-debug-bg shadow-lg md:w-56"
    >
      {/* ヘッダー（メニュー側のトグルで mount/unmount するため常時展開） */}
      <div className="flex items-center gap-2 px-3 py-2 text-xs font-mono">
        <span className="font-bold text-reader-debug-fg-strong">DEBUG</span>
        <button
          type="button"
          onClick={onClearProgress}
          className="ml-auto rounded border border-reader-debug-danger-border bg-reader-debug-danger-bg px-1.5 py-0.5 text-[10px] text-reader-debug-danger-fg hover:bg-reader-debug-danger-bg-hover"
        >
          Clear
        </button>
      </div>

      <div className="overflow-y-auto px-3 pb-3 text-xs font-mono text-reader-debug-fg border-t border-reader-debug-border-subtle">
        <div className="h-2" />
        {/* モデル選択セレクタ（要件 2.1, 2.5） */}
        <div className="mb-2">
          <label className="block text-reader-debug-fg-muted mb-0.5">
            <span className="block">model select</span>
            <select
              value={selectedModel}
              onChange={(e) => onModelChange(e.target.value as AvailableModel)}
              className="w-full bg-reader-debug-bg-input text-reader-debug-fg border border-reader-debug-border rounded px-1 py-0.5 text-xs font-mono"
            >
              {availableModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
        <table className="w-full">
          <tbody>
            <Row label="playline" value={playlineId ? `${playlineId.slice(0, 12)}…` : "—"} />
            <Row label="scene" value={`${sceneNumber} / ${maxScenes} (残り ${remaining})`} />
            <Row label="last gen" value={lastGenerationMs !== null ? formatMs(lastGenerationMs) : "—"} />
            <Row label="avg gen" value={avgGenerationMs !== null ? formatMs(avgGenerationMs) : "—"} />
            <Row
              label="ETA ending"
              value={estimatedRemainingMs !== null ? `~${formatMs(estimatedRemainingMs)}` : "—"}
            />
            <Row
              label="tokens (in)"
              value={formatTokens(totalTokenInput)}
            />
            <Row
              label="tokens (out)"
              value={formatTokens(totalTokenOutput)}
            />
            <Row
              label="tokens total"
              value={formatTokens(totalTokenInput + totalTokenOutput)}
            />
            <Row label="model" value={model} />
            <Row label="cost" value={`$${cost.toFixed(4)}`} />
          </tbody>
        </table>

        {/* 直近生成時間リスト（要件 4.2） */}
        {recentGenTimes.length > 0 && (
          <div className="mt-2 border-t border-reader-debug-border-subtle pt-2">
            <span className="block text-reader-debug-fg-muted mb-1">gen history</span>
            <ul className="space-y-0.5">
              {recentGenTimes.map((ms, i) => (
                // 同一ミリ秒が連続する場合も index で区別できるようキーに index を含める
                <li key={`${ms}-${i}`} className="text-reader-debug-fg">
                  {formatMs(ms)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* エラー履歴（要件 4.4, 4.5）― recentErrors が空の場合は非表示 */}
        {displayErrors.length > 0 && (
          <div className="mt-2 border-t border-reader-debug-border-subtle pt-2">
            <span className="block text-reader-debug-danger-fg-strong mb-1">errors</span>
            <ul className="space-y-0.5">
              {displayErrors.map((msg, i) => (
                <li key={i} className="text-reader-debug-danger-fg break-all">
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* プロンプトサマリー（要件 4.3）― null の場合は非表示 */}
        {lastPromptSummary !== null && (
          <div className="mt-2 border-t border-reader-debug-border-subtle pt-2">
            <span className="block text-reader-debug-fg-muted mb-1">prompt summary</span>
            {/* 長文に備えて折り返し・スクロール可能にする */}
            <div className="max-h-24 overflow-y-auto text-reader-debug-fg-strong whitespace-pre-wrap break-all">
              {lastPromptSummary}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <tr>
      <td className="pr-2 text-reader-debug-fg-muted">{label}</td>
      <td className="text-right text-reader-debug-fg">{value}</td>
    </tr>
  );
}
